import { BasicUpdater } from 'ballerina-core'
import React from 'react'
import { Route } from '../router'
import { CurrentPage, RouteBuilders, Routes, RouteUpdaters } from '../utils/routes.shared'

export type ExtendedModuleState<moduleState extends {}, allApplicationPages> = moduleState & {
  page: CurrentPage<allApplicationPages>
}

export type Module<modulePages, moduleState extends {}, applicationStateRequirements, applicationPagesRequirements> = {
  new: <allApplicationState extends moduleState & applicationStateRequirements>() => {
    routeBuilders: () => RouteBuilders<modulePages>
    routeUpdaters: () => RouteUpdaters<
      ExtendedModuleState<
        allApplicationState & moduleState & applicationStateRequirements,
        modulePages & applicationPagesRequirements
      >,
      modulePages & applicationPagesRequirements,
      keyof modulePages
    >
    routeObject: () => Routes<
      ExtendedModuleState<
        allApplicationState & moduleState & applicationStateRequirements,
        modulePages & applicationPagesRequirements
      >,
      modulePages & applicationPagesRequirements,
      keyof modulePages
    >
    routes: () => Route<
      BasicUpdater<
        ExtendedModuleState<
          allApplicationState & moduleState & applicationStateRequirements,
          modulePages & applicationPagesRequirements
        >
      >
    >[]
    tryPageSwitch: (
      state: ExtendedModuleState<
        allApplicationState & moduleState & applicationStateRequirements,
        modulePages & applicationPagesRequirements
      >,
      setState: (
        _: BasicUpdater<
          ExtendedModuleState<
            allApplicationState & moduleState & applicationStateRequirements,
            modulePages & applicationPagesRequirements
          >
        >
      ) => void
    ) => JSX.Element | undefined
    header: (
      state: ExtendedModuleState<
        allApplicationState & moduleState & applicationStateRequirements,
        modulePages & applicationPagesRequirements
      >,
      setState: (
        _: BasicUpdater<
          ExtendedModuleState<
            allApplicationState & moduleState & applicationStateRequirements,
            modulePages & applicationPagesRequirements
          >
        >
      ) => void
    ) => JSX.Element
    footer: (
      state: ExtendedModuleState<
        allApplicationState & moduleState & applicationStateRequirements,
        modulePages & applicationPagesRequirements
      >,
      setState: (
        _: BasicUpdater<
          ExtendedModuleState<
            allApplicationState & moduleState & applicationStateRequirements,
            modulePages & applicationPagesRequirements
          >
        >
      ) => void
    ) => JSX.Element
    root: (
      state: ExtendedModuleState<
        allApplicationState & moduleState & applicationStateRequirements,
        modulePages & applicationPagesRequirements
      >,
      setState: (
        _: BasicUpdater<
          ExtendedModuleState<
            allApplicationState & moduleState & applicationStateRequirements,
            modulePages & applicationPagesRequirements
          >
        >
      ) => void
    ) => JSX.Element
  }
  fullScreenPages: Array<keyof modulePages>
  initialState: () => moduleState
}

export type ApplicationBuilder<applicationPages, applicationState extends {}> = {
  addModule: <modulePages, moduleState extends {}>(
    module: Module<modulePages, moduleState, applicationState, applicationPages>
  ) => ApplicationBuilder<applicationPages & modulePages, applicationState & moduleState>
  routeBuilders: () => RouteBuilders<applicationPages>
  routeUpdaters: <
    allApplicationState extends applicationState,
    allApplicationPages extends applicationPages
  >() => RouteUpdaters<
    allApplicationState & applicationState,
    allApplicationPages & applicationPages,
    keyof applicationPages
  >
  routeObject: <allApplicationState extends applicationState, allApplicationPages extends applicationPages>() => Routes<
    allApplicationState & applicationState,
    allApplicationPages & applicationPages,
    keyof applicationPages
  >
  routes: <allApplicationState extends applicationState, allApplicationPages extends applicationPages>() => Route<
    BasicUpdater<ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>>
  >[]
  tryPageSwitch: <allApplicationState extends applicationState, allApplicationPages extends applicationPages>(
    state: ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>,
    setState: (
      _: BasicUpdater<
        ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>
      >
    ) => void
  ) => JSX.Element | undefined
  headers: <allApplicationState extends applicationState, allApplicationPages extends applicationPages>(
    state: ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>,
    setState: (
      _: BasicUpdater<
        ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>
      >
    ) => void
  ) => JSX.Element
  footers: <allApplicationState extends applicationState, allApplicationPages extends applicationPages>(
    state: ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>,
    setState: (
      _: BasicUpdater<
        ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>
      >
    ) => void
  ) => JSX.Element
  roots: <allApplicationState extends applicationState, allApplicationPages extends applicationPages>(
    state: ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>,
    setState: (
      _: BasicUpdater<
        ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>
      >
    ) => void
  ) => JSX.Element
  fullScreenPages: Array<keyof applicationPages>
  initialState: () => applicationState
}

export const applicationBuilder: ApplicationBuilder<{}, {}> = {
  addModule: function <modulePages, moduleState extends {}>(
    this: ApplicationBuilder<{}, {}>,
    nextModule: Module<modulePages, moduleState, {}, {}>
  ) {
    return addModule<{}, {}, modulePages, moduleState>(this, nextModule)
  },
  routeBuilders: () => ({}),
  routeUpdaters: () => ({}),
  routeObject: () => ({}),
  routes: () => [],
  tryPageSwitch: ({}) => undefined,
  headers: () => <></>,
  footers: () => <></>,
  roots: () => <></>,
  fullScreenPages: [],
  initialState: () => ({}),
}

function addModule<
  currentRoutePages,
  currentApplicationState extends {},
  additionalRoutePages,
  additionalModuleState extends {}
>(
  application: ApplicationBuilder<currentRoutePages, currentApplicationState>,
  module: Module<additionalRoutePages, additionalModuleState, currentApplicationState, currentRoutePages>
): ApplicationBuilder<currentRoutePages & additionalRoutePages, currentApplicationState & additionalModuleState> {
  type LocalAppBuilder = ApplicationBuilder<
    currentRoutePages & additionalRoutePages,
    currentApplicationState & additionalModuleState
  >
  type LocalRoutePages = currentRoutePages & additionalRoutePages
  type LocalAppState = currentApplicationState & additionalModuleState
  const newModule = module.new()
  return {
    addModule: function <modulePages, moduleState extends {}>(
      this: LocalAppBuilder,
      nextModule: Module<modulePages, moduleState, currentApplicationState, currentRoutePages>
    ) {
      return addModule<LocalRoutePages, LocalAppState, modulePages, moduleState>(this, nextModule)
    },
    routeBuilders: function (this: LocalAppBuilder) {
      return { ...application.routeBuilders(), ...newModule.routeBuilders() } as RouteBuilders<LocalRoutePages>
    },
    routeUpdaters: function <allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(
      this: LocalAppBuilder
    ) {
      return {
        ...application.routeUpdaters<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>(),
        ...newModule.routeUpdaters(),
      } as RouteUpdaters<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>
    },
    routeObject: function <allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(
      this: LocalAppBuilder
    ) {
      return {
        ...application.routeObject<allApplicationState & LocalAppState, allApplicationPages>(),
        ...newModule.routeObject(),
      }
    },
    routes: function <allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(
      this: LocalAppBuilder
    ): Route<
      BasicUpdater<ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>>
    >[] {
      return [
        ...application.routes<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>(),
        ...(newModule.routes() as any),
      ]
    },
    fullScreenPages: [...application.fullScreenPages, ...module.fullScreenPages],
    tryPageSwitch: function <allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(
      this: LocalAppBuilder,
      state: ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>,
      setState: (
        _: BasicUpdater<ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>>
      ) => void
    ) {
      const res1 = application.tryPageSwitch<
        allApplicationState & LocalAppState,
        allApplicationPages & LocalRoutePages
      >(state, setState)
      if (res1 != undefined) return res1
      const res2 = newModule.tryPageSwitch(state as any, setState as any)
      if (res2 != undefined) return res2
      return undefined
    },
    headers: function <allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(
      this: LocalAppBuilder,
      state: ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>,
      setState: (
        _: BasicUpdater<ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>>
      ) => void
    ): JSX.Element {
      return (
        <>
          {application.headers<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>(
            state,
            setState
          )}
          {newModule.header(state as any, setState as any)}
        </>
      )
    },
    footers: function <allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(
      this: LocalAppBuilder,
      state: ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>,
      setState: (
        _: BasicUpdater<ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>>
      ) => void
    ): JSX.Element {
      return (
        <>
          {application.footers<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>(
            state,
            setState
          )}
          {newModule.footer(state as any, setState as any)}
        </>
      )
    },
    roots: function <allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(
      this: LocalAppBuilder,
      state: ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>,
      setState: (
        _: BasicUpdater<ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>>
      ) => void
    ): JSX.Element {
      return (
        <>
          {application.roots<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>(
            state,
            setState
          )}
          {newModule.root(state as any, setState as any)}
        </>
      )
    },
    initialState: function (this: LocalAppBuilder): LocalAppState {
      return { ...application.initialState(), ...module?.initialState() }
    },
  }
}

export type GetApplicationState<applicationBuilder> = applicationBuilder extends ApplicationBuilder<infer rs, infer as>
  ? as
  : 'Given type applicationBuilder does not extend ApplicationBuilder'

export type GetApplicationPages<applicationBuilder> = applicationBuilder extends ApplicationBuilder<infer rs, infer as>
  ? rs
  : 'Given type applicationBuilder does not extend ApplicationBuilder'
